/**
 * Where to go back to after signing in.
 *
 * Opening a link to a particular page — a person's history, an event's
 * register — while signed out used to land you on the dashboard with the
 * page you actually wanted forgotten, so the link had to be found again.
 * The destination is carried through the login as a `next` parameter and
 * returned to afterwards.
 */

const PARAM = 'next'

/** The path a visitor is on, in the form `next` expects. */
export function currentPath(location = window.location) {
  return `${location.pathname}${location.search}${location.hash}`
}

/**
 * Only a path within this app is ever followed.
 *
 * `next` reaches us from the address bar, so a value like
 * `https://elsewhere.example/` or `//elsewhere.example` would otherwise
 * turn our own login into a redirector to someone else's site. A
 * destination must start with a single slash and go nowhere near the login
 * page itself, which would loop.
 */
export function safeNext(value, fallback = '/dashboard') {
  if (!value || typeof value !== 'string') return fallback
  if (!value.startsWith('/') || value.startsWith('//')) return fallback
  if (value.startsWith('/login') || value === '/' || value.startsWith('/reset'))
    return fallback
  return value
}

/** The login URL that will come back to `path` once signed in. */
export function loginUrlFor(path) {
  const destination = safeNext(path, null)
  return destination
    ? `/login?${PARAM}=${encodeURIComponent(destination)}`
    : '/login'
}

/** The destination held in a location's query string, if any. */
export function nextFrom(search) {
  const params = new URLSearchParams(search || '')
  return safeNext(params.get(PARAM))
}
